<template>
  <div class="expandable-settings-item" :class="{ expand: indicatorsExpanded }">
    <div class="expandable-settings-item__title" @click="indicatorsExpanded = !indicatorsExpanded">
      <Icon :rotate-left="!indicatorsExpanded">{{ arrowDownLine }}</Icon>
      {{ $t('message.SETTINGS_INDICATORS_THEMES') }}
    </div>
    <div v-if="indicatorsExpanded" class="settings-theme">
      <div class="generic-background">
        <div class="h-v-centerer">
          <Switcher v-model="selectedView" class="views-switcher" :titles="views" />
        </div>
        <!-- Theme list -->
        <Draggable
          :value="displayedThemes"
          v-bind="themesDragOptions"
          @input="onOrderChange"
        >
          <div
            v-for="(theme, themeIndex) in displayedThemes"
            :key="`theme${theme.id}`"
            class="settings-theme-row"
            :style="itemSectionStyle(theme, themeIndex)"
          >
            <div class="settings-item" @click="theme.expanded = !theme.expanded">
              <div class="v-centerer settings-theme-row__wrapper">
                <div
                  v-if="isThemeGw(theme)"
                  v-quick-title="$t('message.INDIC_SETTINGS_THEME_IS_GW')"
                  class="a-from-support h-v-centerer from-gw"
                >
                  <div class="triangle" />
                  <Icon>{{ hardhatExtraBold }}</Icon>
                </div>
                <DraggingIndication />
                <Icon class="indic-arrow" :style="arrowStyle(theme)">{{ arrowDownLine }}</Icon>
                <div class="theme-name">
                  <EditableLabel v-model="theme.name" generic-border @input="editTheme(theme, 'name')" />
                </div>
                <label class="theme-letter-label" :for="`theme-letter${theme.id}`">
                  {{ $t('message.THEME_LETTER') }}
                </label>
                <div class="theme-letter">
                  <EditableLabel
                    v-model="theme.letter"
                    :placeholder="$t('message.THEME_LETTER')"
                    generic-border
                    @input="editTheme(theme, 'letter')"
                  />
                </div>
              </div>
              <div
                class="generic-border generic-border--icon-button generic-border--red settings-theme-row__delete"
                @click.stop="deleteItem(theme)"
              >
                <Icon>{{ deleteMedium }}</Icon>
              </div>
            </div>
            <div v-if="theme.expanded">
              <!-- Indicator list -->
              <Draggable
                :value="theme.indicators"
                v-bind="dragOptions"
                @input="onOrderChange($event, theme.id)"
                @start="onDragIndicStart($event, theme)"
              >
                <IndicatorsSettingsItem
                  v-for="(indic, indicIndex) in theme.indicators"
                  :key="`indic${indic.id}`"
                  :indicator="indic"
                  :all-themes="themes"
                  :style="itemSectionStyle(indic, indicIndex)"
                  :class="{ expanded: indic.expanded }"
                  :calculation-disabled="config"
                  @edit="edit"
                  @delete="deleteItem(indic, indicIndex)"
                />
              </Draggable>
              <!-- END Indicator list -->
              <!-- Indicator list actions -->
              <button class="add-button" @click="addIndicator(theme)">
                <Icon>{{ addCircleMedium }}</Icon>
                {{ $t('message.ADD_INDICATOR') }}
              </button>
              <button class="add-button" @click="showLibraryIndicators(theme)">
                <Icon>{{ addCircleMedium }}</Icon>
                {{ $t('message.ADD_INDICATOR_FROM_LIBRARY') }}
              </button>
              <button class="add-button" @click="addPublicIndicator(theme)">
                <Icon>{{ addCircleMedium }}</Icon>
                {{ $t('message.IMPORT_PUBLIC_INDICATOR') }}
              </button>
            </div>
          </div>
        </Draggable>
        <!-- END Theme list -->
        <!-- Theme list actions -->
        <button class="add-button" @click="addTheme()">
          <Icon>{{ addCircleMedium }}</Icon>
          {{ $t('message.ADD_THEME') }}
        </button>
        <EditableLabelRadio
          ref="publicIndicators"
          v-model="publicIndicatorId"
          :items="publicIndicators"
          no-text
          ok-button
          label-key="label"
          @input="publicIndicatorSelected"
          @ok="publicIndicatorSelected"
        />
        <EditableLabelRadio
          ref="libraryIndicators"
          v-model="libraryIndicatorId"
          :items="libraryIndicators"
          no-text
          ok-button
          label-key="name"
          @input="libraryIndicatorSelected"
          @ok="libraryIndicatorSelected"
        />
        <EditableLabelRadio
          ref="undisplayedThemes"
          v-model="undisplayedSelectedTheme"
          :items="radioUndisplayedThemes"
          :hint="$t('message.INDIC_SETTINGS_GW_NEW_THEME_HINT')"
          no-text
          ok-button
          label-key="name"
          @input="undisplayedThemeSelected"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Common settings for editing indicators of a team or a scheme
 *
 * @vue-prop {Array} themes - Indicator themes to edit
 * @vue-prop {String} urlPrefix - URL to use. Default is '/server/indicators/' for team indicators
 * @vue-prop {Number} schemeId - Scheme apram to add to the XHR
 * @vue-prop {Boolean} config - Whether to add the config param o the XHR
 */

import axios from 'axios';
import { cloneDeep, isEqual } from 'lodash';
import Draggable from 'vuedraggable';
import toastr from 'toastr';
// Vuex
import { FETCH_LIBRARY_INDICATORS } from '@/store/action-types';
import { GET_LIBRARY_INDICATORS } from '@/store/mutation-types';
// Components
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import IndicatorsSettingsItem from '@/components/indicators-settings-item.vue';
import Switcher from '@/components/switcher.vue';
// Utils
import { calert, decodeHTML } from '@/utils/main';
import {
  addCircleMedium,
  arrowDownLine,
  deleteMedium,
  hardhatExtraBold,
} from '@/assets/icons';

export default {

  name: 'IndicatorsSettings',

  components: {
    Draggable,
    DraggingIndication,
    EditableLabel,
    EditableLabelRadio,
    Icon,
    IndicatorsSettingsItem,
    Switcher,
  },

  props: {
    themes: {
      type: Array,
      required: true,
    },
    urlPrefix: {
      type: String,
      default: '/server/indicators/',
    },
    schemeId: {
      type: Number,
      default: NaN,
    },
    config: Boolean,
  },

  data() {
    return {
      addCircleMedium,
      arrowDownLine,
      deleteMedium,
      hardhatExtraBold,
      indicatorsExpanded: false,
      radioObjItems: [
        { id: 0, label: this.$t('message.MINIMUM') },
        { id: 1, label: this.$t('message.MAXIMUM') },
      ],
      chartTypeItems: [
        { id: 0, label: this.$t('message.CHART_LINE') },
        { id: 1, label: this.$t('message.CHART_BAR') },
      ],
      sectionColors: ['rgba(0, 0, 255, 0.02)', 'rgba(0, 255, 0, 0.02)', 'rgba(255, 0, 255, 0.02)'],
      borderColors: ['rgba(0, 0, 255, 0.12)', 'rgba(0, 255, 0, 0.12)', 'rgba(255, 0, 255, 0.12)'],
      publicIndicatorId: 0,
      allPublicIndicators: [],
      publicIndicators: [],
      choosingPublicIndicator: false,
      pickingPublicForTheme: null,
      libraryIndicatorId: 0,
      pickingLibraryForTheme: null,
      /** Draggable */
      indicatorDragged: null,
      averageItems: [
        { id: 0, label: this.$t('message.CUMULATE') },
        { id: 1, label: this.$t('message.CALC_AVERAGE') },
      ],
      // View : GW / Classic
      views: [
        this.$t('message.INDIC_SETTINGS_REGULAR'),
        this.$t('message.GW'),
      ],
      // ID of the new them of an indicator when checking/unchecking 'isGw'
      undisplayedSelectedTheme: 0,
      // Indicator whose theme is being edited
      themeEditionIndicator: null,
      // Booleans to check for new themes or indicators and expand theme automatically
      pendingThemeAddition: false,
      pendingIndicatorAddition: false,
      // Object where the key is the param name and the value is the cancelToken of the request sent to edit this param
      xhrCancelPerParamName: {},
    };
  },

  computed: {
    /** Object: Props bound to indicators' Draggable */
    dragOptions() {
      return {
        animation: 200,
        disabled: false,
        ghostClass: 'drag-ghost',
        group: 'indicators',
      };
    },
    /** Object: Props bound to themes' Draggable */
    themesDragOptions() {
      return { ...this.dragOptions, group: 'themes' };
    },
    libraryIndicators() {
      const themes = cloneDeep(this.$store.state[GET_LIBRARY_INDICATORS.stateKey] || []);
      return themes.map(theme => ({ title: theme.name, radioItems: theme.indicators }));
    },
    sParams() {
      return { includeIndicatorsGWusage: true, scheme: this.schemeId };
    },
    /** Array: of themes displayed according to 'selectedView' */
    displayedThemes() {
      return this.themes.filter(theme => !theme.indicators.some(indic => indic.isGw !== this.selectedView));
    },
    /** Array: of themes not displayed according to 'selectedView' */
    undisplayedThemes() {
      return this.themes.filter(theme => !theme.indicators.some(indic => indic.isGw === this.selectedView));
    },
    /** Array: of themes not displayed according to 'selectedView' */
    radioUndisplayedThemes() {
      const title = this.selectedView === 0
        ? this.$t('message.INDIC_SETTINGS_GW_THEMES')
        : this.$t('message.INDIC_SETTINGS_REGULAR_THEMES');
      return [{ title, radioItems: this.undisplayedThemes }];
    },
    /** Number: Index of the selected view */
    selectedView: {
      get() {
        return Number(this.$route.query.indicSettingsSelectedView) === 1 ? 1 : 0;
      },
      set(index) {
        const query = Object.assign({}, this.$route.query);
        query.indicSettingsSelectedView = index === 1 ? 1 : undefined;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    themes(newVal, oldVal) {
      // Check ids of themes and indicators that have just been added to expand them automatically
      let latestTheme;
      if (this.pendingThemeAddition) {
        const prevThemeIds = oldVal.map(t => t.id);
        const newThemeIds = newVal
          .map(t => t.id)
          .filter(id => !prevThemeIds.includes(id));
        latestTheme = Math.max(newThemeIds);
      }
      let latestIndic;
      if (this.pendingIndicatorAddition) {
        const prevIndicIds = oldVal.flatMap(t => t.indicators.map(i => i.id));
        const newIndicIds = newVal
          .flatMap(t => t.indicators.map(i => i.id))
          .filter(id => !prevIndicIds.includes(id));
        latestIndic = Math.max(newIndicIds);
      }

      this.themes.forEach((t) => {
        t.expanded = this.pendingThemeAddition && t.id === latestTheme ? true : t.expanded;
        t.indicators.forEach((i) => {
          i.expanded = this.pendingIndicatorAddition && i.id === latestIndic ? true : i.expanded;
        });
      });
      this.pendingIndicatorAddition = false;
      this.pendingThemeAddition = false;
    },
  },

  created() {
    this.fetchLibraryIndicators();
    this.indicatorsExpanded = this.config;
  },

  beforeDestroy() {
    clearTimeout(this.objectiveTimeout);
  },

  methods: {
    decodeHTML,
    fetchLibraryIndicators() {
      const payload = {};
      if (this.config) {
        payload.config = 1;
      }
      this.$store.dispatch(FETCH_LIBRARY_INDICATORS, payload);
    },
    /**
     * Check if a theme is a GW theme
     * @param  {Object}  theme
     * @return {Boolean}        true if GW theme, false otherwise
     */
    isThemeGw(theme) {
      return theme.indicators.length > 0 && !theme.indicators.some(indicator => indicator.isGw === 0);
    },
    /** Drag event triggered on an indicator */
    onDragIndicStart(evt, theme) {
      if (evt.oldIndex !== undefined) {
        this.indicatorDragged = theme.indicators[evt.oldIndex] || null;
      }
    },
    /** Drag event ended for a theme or an indicator */
    onOrderChange(newList, ofTheme) {
      if (ofTheme === undefined) {
        // Append GW indics or prepend dmp indic in the ordered list
        // when changing the order of themes
        if (this.selectedView === 0) {
          newList = [...newList, ...this.undisplayedThemes];
        } else {
          newList = [...this.undisplayedThemes, ...newList];
        }
      }
      const params = {
        ...this.sParams,
        ids: newList.map(i => i.id),
        theme: ofTheme || 0,
      };
      if (this.indicatorDragged) {
        params.oldTheme = this.indicatorDragged.theme;
      }
      this.$http.post(`${this.urlPrefix}setOrder.php`, params).then((response) => {
        if (response && response.data instanceof Array) {
          this.$emit('update', response.data);
        }
      });
      this.indicatorDragged = null;
    },
    /** Emit an event for the parent to know that indicators have been updated */
    emitUpdate(response, expandAddedTheme = false, expandAddedIndicator = false) {
      if (response && response.data instanceof Array) {
        const { data } = response;
        this.pendingThemeAddition = expandAddedTheme;
        this.pendingIndicatorAddition = expandAddedIndicator;
        this.$emit('update', data);
      }
    },
    addTheme() {
      this.$http.post(`${this.urlPrefix}addTheme.php`, this.sParams).then((response) => {
        this.emitUpdate(response, true);
      });
    },
    addIndicator(theme) {
      this.$http.post(`${this.urlPrefix}add.php`, {
        ...this.sParams,
        theme: theme.id,
        isGw: this.selectedView,
      })
        .then((response) => {
          this.emitUpdate(response, false, true);
        });
    },
    editTheme(theme, field) {
      const params = { id: theme.id, ...this.sParams };
      params[field] = theme[field];
      this.App.post(`${this.urlPrefix}editTheme.php`, params, (d) => {
        if (d instanceof Array) {
          this.$emit('update', d);
        }
      });
    },
    /** Set a timeout to the objective edition */
    tempEditObjective(indicator) {
      clearTimeout(this.objectiveTimeout);
      this.objectiveTimeout = setTimeout(() => { this.edit(indicator, 'objective'); }, 250);
    },
    /**
     * Update a DB indicator
     * @param  {Object} indic - indicator object to update
     * @param  {String|Array} fieldKeys - key of the indicator object that will be updated
     * @param  {Object} [fieldValues=undefined] - if set, send fieldValues[fieldKey] to the server.
     *                                            Else ends indicator[fieldKey]
     */
    edit(indic, fieldKeys, fieldValues) {
      if (fieldKeys === 'isGw') {
        // A new theme must be choosen when changing isGw param
        this.$refs.undisplayedThemes.edit();
        this.themeEditionIndicator = indic;
        return;
      }
      if (!(fieldKeys instanceof Array)) {
        fieldKeys = [fieldKeys];
      }
      const params = { id: indic.id, ...this.sParams };
      fieldKeys.forEach((key) => {
        params[key] = fieldValues ? fieldValues[key] : indic[key];
        if (this.xhrCancelPerParamName[key]) {
          this.xhrCancelPerParamName[key]();
        }
      });
      const xhrOpts = {
        method: 'post',
        url: `${this.urlPrefix}edit.php`,
        data: params,
        cancelToken: new axios.CancelToken((cancel) => {
          fieldKeys.forEach((key) => {
            this.xhrCancelPerParamName[key] = cancel;
          });
        }),
      };
      this.$http(xhrOpts)
        .then((response) => {
          if (response && response.data instanceof Array) {
            this.$emit('update', response.data);
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            // The XHR has been canceled because a new one started
          } else if (!err.response || err.response.status !== 401) {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
    },
    /** A new theme has been selected for an indicator, after ticking 'isGw' */
    undisplayedThemeSelected(themeId) {
      if (themeId && this.themeEditionIndicator) {
        this.edit(
          this.themeEditionIndicator,
          ['isGw', 'theme'],
          { isGw: (this.selectedView + 1) % 2, theme: themeId },
        );
      }
    },
    deleteItem(item, indicIndex) {
      const theme = typeof indicIndex === 'undefined';
      const deleteWhat = this.$t(`message.${theme ? 'INDIC_SETTINGS_DELETE_THEME' : 'INDIC_SETTINGS_DELETE_INDIC'}`);
      const buttons = [
        {
          title: deleteWhat,
          sClass: 'red',
          action: () => {
            this.confirmDeleteItem(item, theme);
          },
        },
        { title: this.$t('message.CANCEL') },
      ];
      if (
        (!theme && item.isGw === 1 && item.gwItems && item.gwItems.length)
        || (theme && this.isThemeGw(item))
      ) {
        buttons.unshift({
          title: this.$t('message.INDIC_SETTINGS_DELETE_ORPHAN_ITEMS', { deleteWhat }),
          sClass: 'red',
          action: () => {
            this.confirmDeleteItem(item, theme, { deleteOrphanGwItems: true });
          },
        });
      }
      calert(this.$t(`message.${theme ? 'CONFIRM_DELETE_THEME' : 'CONFIRM_DELETE_INDICATOR'}`, [item.name]), buttons);
    },
    /**
     * [confirmDeleteItem description]
     * @param  {Object} item         Indicator or theme object to delete
     * @param  {Boolean} theme       Whether this is a theme
     * @param  {Object} [postParams] Additional post params to send in the XHR
     */
    confirmDeleteItem(item, theme, postParams = {}) {
      const params = { id: item.id, theme, ...this.sParams, ...postParams };
      this.$http.post(`${this.urlPrefix}delete.php`, params).then((response) => {
        if (response && response.data instanceof Array) {
          this.$emit('update', response.data);
        }
      });
    },
    showLibraryIndicators(theme) {
      this.pickingLibraryForTheme = theme;
      this.fetchLibraryIndicators();
      this.$refs.libraryIndicators.edit();
    },
    libraryIndicatorSelected() {
      this.$http.post(`${this.urlPrefix}add.php`, {
        ...this.sParams,
        theme: this.pickingLibraryForTheme.id,
        library: this.libraryIndicatorId,
        isGw: this.selectedView,
      }).then((response) => {
        this.emitUpdate(response, false, true);
        this.libraryIndicatorId = 0;
      });
    },
    /** Indicators set may contain an indicator from another team to be displayed */
    addPublicIndicator(theme) {
      this.pickingPublicForTheme = theme;
      this.$http.post(`${this.urlPrefix}get.php`, { indicatorsOnly: true, public: true }).then((response) => {
        if (response && response.data instanceof Array) {
          const d = response.data;
          this.allPublicIndicators = response.data;
          const teams = {};
          this.allPublicIndicators.forEach((indic, i) => {
            if (Number(indic.teamId) !== this.currentTeam.id) {
              indic.label = `${indic.name} (${indic.themeName})`;
              if (!teams[indic.teamName]) {
                teams[indic.teamName] = [];
              }
              teams[indic.teamName].push(d[i]);
            }
          });
          this.publicIndicators = [];
          Object.keys(teams).forEach((name) => {
            this.publicIndicators.push({
              title: decodeHTML(name),
              radioItems: teams[name],
            });
          });
          this.$refs.publicIndicators.edit();
        }
      });
    },
    /** Public indicators input changed (ok clicked) */
    publicIndicatorSelected() {
      this.$http.post(`${this.urlPrefix}add.php`, {
        ...this.sParams,
        theme: this.pickingPublicForTheme.id,
        public: this.publicIndicatorId,
        isGw: this.selectedView,
      }).then((response) => {
        if (response && response.data instanceof Array) {
          this.$emit('update', response.data);
        }
      });
    },
    arrowStyle(theme) {
      return theme.expanded ? 'transform: rotate(0deg)' : 'transform: rotate(-90deg)';
    },
    itemSectionStyle(item, itemIndex) {
      if (item.expanded) {
        return {
          border: `2px solid ${this.borderColors[itemIndex % this.borderColors.length]}`,
          backgroundColor: this.sectionColors[itemIndex % this.sectionColors.length],
        };
      }
      return {};
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.settings-indicators {

  .views-switcher.switcher {
    margin: 0.5em;
  }
}

.settings-indic {
  transition: background 0.2s;
  position: relative;
}
.settings-theme .indic-arrow {
  width: 11px;
  transition: transform 0.2s ease-in-out;
}
.settings-theme .theme-title {
  cursor: pointer;
}
.settings-theme input {
  padding: 3px 8px;
  color: #58595B;
}
.settings-theme .theme-name,
.settings-theme .theme-letter,
.indic-details input {
  margin: 0px 10px;
  padding: 2px 5px;
  overflow: hidden;

  &.theme-name {
    width: 40%;
  }
  &.theme-letter {
    width: 10%;
  }
}
.settings-theme .theme-name {
  margin-right: 5%;
}

.settings-theme .add-button {
  margin: 15px 0px 15px 50px;
}

.settings-theme .objective-wrapper {
  vertical-align: bottom;
  display: inline-block;

  &.top {
    vertical-align: top;
    margin-top: 10px;
  }
}
.settings-theme .input-number {
  width: 50px;
}

.settings-theme-row {
  background: white;
  position: relative;
  border-radius: $main-border-radius;
  margin-bottom: .7rem;

  .settings-item {
    background: transparent;
    position: relative;
    padding: 1rem 2.5rem;
    height: auto;
    min-height: 0px;
    justify-content: space-between;

    .triangle {
      pointer-events: none;
    }
  }

  .dragging-indication {
    position: absolute;
    left: 1.2rem;
    top: 50%;
    transform: translateY(-50%);
  }

  &__wrapper {
    flex: 1;
    min-width: 0px;
  }
}
.settings-indic .a-from-support {
  width: 30px;
  height: 30px;
}
.settings-indic .a-from-support .triangle {
  border-width: 15px;
}
.settings-indic .a-from-support .text {
  font-weight: 600;
  font-size: 0.9em;
  transform: rotate(45deg) translateY(9px);
}
.indic-details .disabled {
  opacity: 0.7;
}
.small-checkbox {
  overflow: hidden;
  height: 0px;

  &.expanded {
    height: auto;
  }
  .checkbox {
    margin-left: 25px;
    padding: 0px;

    > img {
      width: 10px;
    }
    > span {
      font-size: 0.95em;
    }
  }
}
/** Draggable classes */
.drag-ghost,
.drag-ghost > .settings-item {
  opacity: 0.5;
  background-color: #d6e2f5;
}
.flip-list-move {
  transition: transform 0.5s;
}
.no-move {
  transition: transform 0s;
}

@media (max-width: $smartphone-landscape) {
  .settings-theme .objective-wrapper {
    &.top {
      margin-top: 5px;
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .settings-theme .settings-item {
    padding: 0.5em 0.5em;

    .theme-letter-label {
      display: none;
    }
  }
}


</style>
